package at.theatervogelweide.admin.web.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import at.theatervogelweide.admin.contract.Account
import at.theatervogelweide.admin.web.Route
import kotlinx.browser.document
import org.w3c.dom.Element
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Aside
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Main
import org.jetbrains.compose.web.dom.Nav
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

private val shell = Bem("admin-shell")
private val nav = Bem("admin-nav")
private val alert = Bem("admin-alert")

/**
 * A navigation entry; only add one when the screen behind it exists. [beta] marks a screen only the new website shows
 * with a [BetaBadge] (#144).
 */
data class NavigationEntry(val label: String, val route: Route, val beta: Boolean = false)

/**
 * Navigation entries under a visible [title] ("Website", "Verwaltung"), or without one for those before the first
 * group (Übersicht). A group stays even with a single entry (#144).
 */
data class NavigationGroup(val title: String?, val entries: List<NavigationEntry>) {
    /** The id of the title, which names the group, e.g. `nav-website`. */
    val titleId: String? = title?.let { "nav-" + it.lowercase() }
}

/**
 * The frame of every screen: sidebar with the club mark, navigation and account footer, beside the main column.
 * The root element carries `admin-root` already (see index.html). Each screen fills the main column with an
 * [AdminPageHeader] followed by an [AdminPageBody] or an [AdminSplitView]. [signOutError] says below "Abmelden" why
 * signing out failed. The content learns from [LocalPanes] whether the shell is wide enough for panes.
 */
@Composable
fun AdminShell(
    account: Account,
    navigation: List<NavigationGroup>,
    current: Route,
    onNavigate: (Route) -> Unit,
    onSignOut: () -> Unit,
    signOutError: String? = null,
    content: @Composable () -> Unit,
) {
    // until the shell is laid out, the window's width is the best guess
    var panes by remember { mutableStateOf(panesFit(document.documentElement?.clientWidth?.toDouble() ?: 0.0)) }

    val element = remember { arrayOfNulls<Element>(1) }

    Div({
        classes(shell.block)
        ref {
            element[0] = it
            onDispose { element[0] = null }
        }
    }) {
        Aside({ classes(shell.element("sidebar").className) }) {
            Div({ classes(shell.element("brand").className) }) {
                Span({
                    classes(shell.element("mark").className)
                    attr("aria-hidden", "true")
                }) { Text("t") }
                Span({ classes(shell.element("brand-text").className) }) {
                    Span({ classes(shell.element("brand-name").className) }) { Text("Theater Vogelweide") }
                    Span({ classes(shell.element("brand-area").className) }) { Text("Verwaltung") }
                }
            }

            Nav({
                classes(nav.block)
                attr("aria-label", "Verwaltung")
            }) {
                navigation.forEach { group ->
                    val titleId = group.titleId
                    if (group.title == null || titleId == null) {
                        group.entries.forEach { NavigationLink(it, current, onNavigate) }
                    } else {
                        // a group, not a heading: the page's h1 stays the first heading
                        Div({
                            classes(nav.element("group").className)
                            attr("role", "group")
                            attr("aria-labelledby", titleId)
                        }) {
                            Span({
                                classes(nav.element("group-title").className)
                                id(titleId)
                            }) { Text(group.title) }
                            group.entries.forEach { NavigationLink(it, current, onNavigate) }
                        }
                    }
                }
            }

            Div({ classes(shell.element("footer").className) }) {
                PublicSiteLink("/") { Text("Zur Website ↗") }
                BetaSiteLink { Text("Neue Website (Beta) ↗") }
                Span({ classes(shell.element("account").className) }) { Text("angemeldet als ${account.email}") }
                A(Route.ChangePassword.path, {
                    classes(shell.element("footer-link").className)
                    if (current == Route.ChangePassword) {
                        attr("aria-current", "page")
                    }
                    onClick { event ->
                        if (!event.ctrlKey && !event.metaKey && !event.shiftKey) {
                            event.preventDefault()
                            onNavigate(Route.ChangePassword)
                        }
                    }
                }) { Text("Passwort ändern") }
                A(Route.SignIn.PATH, {
                    classes(shell.element("logout").className)
                    onClick { event ->
                        event.preventDefault()
                        onSignOut()
                    }
                }) { Text("Abmelden") }
                signOutError?.let {
                    P({
                        classes(alert.block)
                        attr("role", "alert")
                    }) { Text(it) }
                }
            }
        }

        Main({ classes(shell.element("main").className) }) {
            CompositionLocalProvider(LocalPanes provides panes) {
                content()
            }
        }
    }

    // after the shell's ref, once the shell is in its document, whose window's ResizeObserver has to watch it
    DisposableEffect(Unit) {
        val observer = element[0]?.let { shellElement -> observeWidth(shellElement) { panes = panesFit(it) } }
        onDispose { observer?.disconnect() }
    }
}

@Composable
private fun NavigationLink(entry: NavigationEntry, current: Route, onNavigate: (Route) -> Unit) {
    A(entry.route.path, {
        classes(nav.element("item").className)
        // a screen stays current with a record selected, e.g. /neuigkeiten/42
        if (entry.route::class == current::class) {
            attr("aria-current", "page")
        }
        onClick { event ->
            if (!event.ctrlKey && !event.metaKey && !event.shiftKey) {
                event.preventDefault()
                onNavigate(entry.route)
            }
        }
    }) {
        Text(entry.label)
        if (entry.beta) {
            BetaBadge()
        }
    }
}

private val pageHeader = Bem("admin-page-header")

/**
 * The white bar at the top of every screen, with its one `h1`. With [unsavedChanges] it says so beside the
 * actions, for screens whose editor has changes that aren't saved yet. [breadcrumb] goes above the title, e.g. the way
 * back from a record to its list.
 */
@Composable
fun AdminPageHeader(
    title: String,
    unsavedChanges: Boolean = false,
    /** Whether only the new website shows the screen, which a [BetaBadge] after the title says (#144). */
    beta: Boolean = false,
    breadcrumb: (@Composable () -> Unit)? = null,
    actions: (@Composable () -> Unit)? = null,
) {
    Div({ classes(pageHeader.block) }) {
        Div({ classes(pageHeader.element("titles").className) }) {
            breadcrumb?.let {
                Nav({
                    classes(pageHeader.element("breadcrumb").className)
                    attr("aria-label", "Pfad")
                }) { it() }
            }
            org.jetbrains.compose.web.dom.H1({ classes(pageHeader.element("title").className) }) {
                Text(title)
                if (beta) {
                    BetaBadge()
                }
            }
        }
        Div({ classes(pageHeader.element("actions").className) }) {
            // always in the page, so screen readers announce the note when it appears
            Span({
                classes(pageHeader.element("note").className)
                attr("role", "status")
            }) {
                if (unsavedChanges) {
                    Text("Ungespeicherte Änderungen")
                }
            }
            actions?.invoke()
        }
    }
}

/**
 * The content of a screen below its [AdminPageHeader]: the cards, stacked. [block] mixes in the screen's own block,
 * so its elements are in scope (e.g. `admin-overview`).
 */
@Composable
fun AdminPageBody(block: String? = null, content: @Composable () -> Unit) {
    Div({ classes(*listOfNotNull(shell.element("body").className, block).toTypedArray()) }) {
        content()
    }
}
